import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, FileText, Loader, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';
import { generateDocumentHtml } from '../utils/documentHtml';

// Shared by tournaments and academy programs — the owner supplies the endpoint.
export default function DocumentViewerModal({ type, tournamentId, docPath, onClose }) {
  const { theme } = useTheme();
  const isDark = theme.background !== 'bg-white';
  const [srcDoc, setSrcDoc] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const title = type === 'terms' ? 'Terms & Conditions' : 'Liability Waiver';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiCall(docPath ?? `/tournaments/${tournamentId}/documents/${type}`);
        if (res.format === 'pdf') {
          setPdfUrl(res.url);
        } else if (res.format === 'json') {
          setSrcDoc(generateDocumentHtml(res.data));
        } else {
          setSrcDoc(res.html);
        }
      } catch {
        setError('Failed to load document. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tournamentId, type, docPath]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme.border}`}>
          <div className="flex items-center gap-x-3">
            <FileText className="w-5 h-5 text-blue-500" />
            <div>
              <h2 className={`font-semibold text-base ${theme.text}`}>{title}</h2>
              <p className={`text-xs ${theme.textMuted}`}>Document preview</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${theme.textMuted}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden min-h-0 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full py-16">
              <Loader className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full py-16 space-y-2 text-center">
              <AlertCircle className="w-8 h-8 text-gray-400" />
              <p className={`text-sm ${theme.textMuted}`}>{error}</p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title={title}
              className="w-full h-full rounded-lg"
              style={{ minHeight: '420px', border: 'none' }}
            />
          ) : (
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <iframe
                srcDoc={srcDoc}
                title={title}
                className="w-full"
                style={{ height: '420px', border: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end px-5 py-4 border-t ${theme.border}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg ${theme.textMuted} hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
